import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import { Box, Button, CircularProgress, Menu, MenuItem, Typography } from '@mui/material';
import { useMutation } from '@apollo/client';
import React, { useCallback, useState } from 'react';

import {
    GET_JOB_APPLICATION_LOGS,
    UPDATE_APPLICATION_PREMIUM_STATUS_UPERCUT,
    UPDATE_JOB_APPLICATION_STATUS,
} from '@gql/jobs';
import {
    JobApplicationStatus,
    JobApplicationSubStatus,
    jobApplicationStatusLabels,
    jobApplicationSubStatusLabels,
    jobApplicationStatuses,
    PremiumJobApplicationStatus,
    PremiumJobApplicationSubStatus,
    premiumJobApplicationStatusLabels,
    premiumJobApplicationSubStatusLabels,
    premiumJobApplicationStatuses,
} from '@modules/jobApplications/statuses';
import { getPremiumStatusColors, getStatusColors } from '@modules/jobApplications/utils';
import PremiumBookingDialog from './PremiumBookingDialog';
import FileManagerModal from '@modules/files/FileManagerModal';

interface ChangeJobApplicationStatusButtonProps {
    jobApplicationId: string;
    jobApplication?: any;
    status?: string;
    subStatus?: string;
    recruitmentMethod?: string;
    /**
     * When true, the button renders the current status but cannot be opened/changed.
     * Used once a premium application has been handed off to the client (sentToClientAt/By set).
     */
    readOnly?: boolean;
    onStatusChanged?: () => void;
}

const REJECTION_SUB_STATUSES = new Set([
    JobApplicationSubStatus.NewRejected,
    JobApplicationSubStatus.InterviewRejected,
    JobApplicationSubStatus.TrialRejected,
    JobApplicationSubStatus.OfferRejected,
]);

const PREMIUM_REJECTION_SUB_STATUSES = new Set([
    PremiumJobApplicationSubStatus.NewRejected,
    // SystemRejected is intentionally NOT here — it is not a final status.
    // The system may mistakenly reject an applicant, so the recruiter
    // must still be able to re-review and change the status.
    // PremiumJobApplicationSubStatus.SystemRejected,
    // ── Client-managed (after DC.Sent, clients handle via status/subStatus) ──
    // PremiumJobApplicationSubStatus.InterviewRejected,
    PremiumJobApplicationSubStatus.TrialRejected,
    // PremiumJobApplicationSubStatus.OfferRejected,
    PremiumJobApplicationSubStatus.Rejected,
    PremiumJobApplicationSubStatus.EmployeeResigned,
    PremiumJobApplicationSubStatus.EmployerTerminated,
]);

const ALL_PREMIUM_STATUSES = Object.values(PremiumJobApplicationStatus);

const ChangeJobApplicationStatusButton: React.FC<ChangeJobApplicationStatusButtonProps> = ({
    jobApplicationId,
    jobApplication,
    status,
    subStatus,
    recruitmentMethod,
    readOnly,
    onStatusChanged,
}) => {
    const isPremium = recruitmentMethod === 'premium';
    const displayStatus = isPremium ? status : status;
    const displaySubStatus = isPremium ? subStatus : subStatus;

    const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
    const [selectedStatus, setSelectedStatus] = useState<string | null>(null);
    const [bookingDialogOpen, setBookingDialogOpen] = useState(false);
    const [pendingBookingStatus, setPendingBookingStatus] = useState<{
        status: string;
        subStatus: string;
    } | null>(null);
    const [bookingMode, setBookingMode] = useState<'book' | 'reschedule'>('book');

    const [fileShareDialogOpen, setFileShareDialogOpen] = useState(false);
    const [fileShareTalentId, setFileShareTalentId] = useState('');
    const [fileShareProId, setFileShareProId] = useState('');

    const [updateStandardStatus, { loading: standardLoading }] = useMutation(UPDATE_JOB_APPLICATION_STATUS);
    const [updatePremiumStatus, { loading: premiumLoading }] = useMutation(UPDATE_APPLICATION_PREMIUM_STATUS_UPERCUT);

    const loading = standardLoading || premiumLoading;
    const isOpen = Boolean(anchorEl);

    const isRejected = isPremium
        ? displayStatus === PremiumJobApplicationStatus.Rejected ||
          PREMIUM_REJECTION_SUB_STATUSES.has((displaySubStatus ?? '') as PremiumJobApplicationSubStatus)
        : displayStatus === JobApplicationStatus.Rejected ||
          REJECTION_SUB_STATUSES.has((displaySubStatus ?? '') as JobApplicationSubStatus);

    const colors = isPremium
        ? getPremiumStatusColors(displayStatus, displaySubStatus)
        : getStatusColors(displayStatus, displaySubStatus);

    const handleOpen = (event: React.MouseEvent<HTMLButtonElement>) => {
        event.stopPropagation();
        setAnchorEl(event.currentTarget);
        setSelectedStatus(null);
    };

    const handleClose = () => {
        setAnchorEl(null);
        setSelectedStatus(null);
    };

    const handleStatusClick = (newStatus: string) => {
        const subs = isPremium
            ? premiumJobApplicationStatuses[newStatus as keyof typeof premiumJobApplicationStatuses] ?? []
            : jobApplicationStatuses[newStatus as keyof typeof jobApplicationStatuses] ?? [];
        if (subs.length === 0) {
            handleSubmit(newStatus, '');
        } else {
            setSelectedStatus(newStatus);
        }
    };

    const handleSubStatusClick = (newSubStatus: string) => {
        if (selectedStatus) {
            if (isPremium && newSubStatus === PremiumJobApplicationSubStatus.Scheduled) {
                setPendingBookingStatus({ status: selectedStatus, subStatus: newSubStatus });
                setBookingMode('book');
                handleClose();
                setBookingDialogOpen(true);
            } else if (
                isPremium &&
                newSubStatus === PremiumJobApplicationSubStatus.ToReschedule &&
                selectedStatus === PremiumJobApplicationStatus.UpercutInterview
            ) {
                setPendingBookingStatus({ status: selectedStatus, subStatus: newSubStatus });
                setBookingMode('reschedule');
                handleClose();
                setBookingDialogOpen(true);
            } else {
                handleSubmit(selectedStatus, newSubStatus);
            }
        }
    };

    const handleSubmit = useCallback(
        async (newStatus: string, newSubStatus: string) => {
            handleClose();
            try {
                if (isPremium) {
                    await updatePremiumStatus({
                        variables: {
                            jobApplicationId,
                            premiumStatus: newStatus,
                            premiumSubStatus: newSubStatus,
                            skipN8n: false,
                        },
                        refetchQueries: [
                            {
                                query: GET_JOB_APPLICATION_LOGS,
                                variables: { jobApplicationId },
                            },
                        ],
                        update: (cache, { data: mutationData }) => {
                            const updated = mutationData?.updateApplicationPremiumStatusUpercut;
                            if (!updated) return;

                            const cacheId = cache.identify({
                                __typename: 'JobApplication',
                                _id: updated._id,
                            });
                            if (!cacheId) return;

                            cache.modify({
                                id: cacheId,
                                fields: {
                                    staleness() {
                                        const s = updated.staleness;
                                        if (!s) return undefined;
                                        return {
                                            __typename: 'JobApplicationStaleness',
                                            level: s.level,
                                            lastUpdateDate: s.lastUpdateDate,
                                            daysSinceUpdate: s.daysSinceUpdate,
                                        };
                                    },
                                },
                            });
                        },
                    });
                } else {
                    await updateStandardStatus({
                        variables: {
                            _id: jobApplicationId,
                            status: newStatus,
                            subStatus: newSubStatus,
                        },
                        refetchQueries: [
                            {
                                query: GET_JOB_APPLICATION_LOGS,
                                variables: { jobApplicationId },
                            },
                        ],
                    });
                }
                if (onStatusChanged) onStatusChanged();

                // Auto-open file sharing dialog when premium DC status goes to InProgress
                if (
                    isPremium &&
                    newStatus === PremiumJobApplicationStatus.DC &&
                    newSubStatus === PremiumJobApplicationSubStatus.InProgress &&
                    jobApplication?.talent?._id &&
                    jobApplication?.jobOffer?.pro?._id
                ) {
                    setFileShareTalentId(jobApplication.talent._id);
                    setFileShareProId(jobApplication.jobOffer.pro._id);
                    setFileShareDialogOpen(true);
                }
            } catch (err) {
                console.error('Failed to update job application status', err);
            }
        },
        [isPremium, jobApplicationId, updatePremiumStatus, updateStandardStatus, onStatusChanged],
    );

    const handleBookingComplete = useCallback(() => {
        setBookingDialogOpen(false);
        setPendingBookingStatus(null);
        setBookingMode('book');
        if (onStatusChanged) onStatusChanged();
    }, [onStatusChanged]);

    const handleBookingClose = useCallback(() => {
        setBookingDialogOpen(false);
        setPendingBookingStatus(null);
        setBookingMode('book');
    }, []);

    const subStatuses = selectedStatus
        ? isPremium
            ? premiumJobApplicationStatuses[selectedStatus as keyof typeof premiumJobApplicationStatuses] ?? []
            : jobApplicationStatuses[selectedStatus as keyof typeof jobApplicationStatuses] ?? []
        : [];

    const selectedStatusColors = isPremium
        ? getPremiumStatusColors(selectedStatus ?? undefined)
        : getStatusColors(selectedStatus ?? undefined);

    const statusList = isPremium
        ? ALL_PREMIUM_STATUSES
        : Object.values(JobApplicationStatus).filter(s => s !== JobApplicationStatus.Rejected);

    return (
        <>
            <Button
                onClick={handleOpen}
                startIcon={loading ? <CircularProgress size={12} color="inherit" /> : undefined}
                disabled={loading || isRejected || readOnly}
                sx={{
                    width: '100%',
                    py: '10px',
                    px: '24px',
                    borderRadius: '12px',
                    minWidth: 0,
                    textTransform: 'none',
                    backgroundColor: colors.background,
                    color: '#fff',
                    fontWeight: 600,
                    fontSize: '16px',
                    lineHeight: '18px',
                    gap: '2px',
                    display: 'inline-flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    '&:hover': {
                        backgroundColor: colors.background,
                        opacity: 0.85,
                    },
                    '& .MuiButton-endIcon': {
                        marginLeft: '2px',
                    },
                    '&.Mui-disabled': {
                        opacity: 0.6,
                        color: '#fff',
                        backgroundColor: colors.background,
                    },
                }}>
                <Box
                    sx={{
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'flex-start',
                    }}>
                    {colors.label}
                    {displaySubStatus && (
                        <Box component="span" sx={{ fontSize: '12px', fontWeight: 400 }}>
                            {colors.subLabel}
                        </Box>
                    )}
                </Box>
                {!isRejected && !readOnly && <ChevronRightIcon sx={{ fontSize: 18 }} />}
            </Button>
            <Menu
                anchorEl={anchorEl}
                open={isOpen}
                onClose={handleClose}
                onClick={e => e.stopPropagation()}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
                transformOrigin={{ vertical: 'top', horizontal: 'left' }}
                slotProps={{
                    paper: {
                        sx: {
                            mt: 1,
                            minWidth: 220,
                            borderRadius: '12px',
                            border: theme => `1px solid ${theme.palette.border.main}`,
                            backgroundColor: theme => theme.palette.background.paper,
                            boxShadow: '0px 8px 24px rgba(0,0,0,0.12)',
                        },
                    },
                }}>
                {!selectedStatus ? (
                    statusList.map(s => {
                        const sColors = isPremium ? getPremiumStatusColors(s) : getStatusColors(s);
                        const subs = isPremium
                            ? premiumJobApplicationStatuses[s as keyof typeof premiumJobApplicationStatuses] ?? []
                            : jobApplicationStatuses[s as keyof typeof jobApplicationStatuses] ?? [];
                        const label = isPremium
                            ? premiumJobApplicationStatusLabels[s as keyof typeof premiumJobApplicationStatusLabels]
                            : jobApplicationStatusLabels[s as keyof typeof jobApplicationStatusLabels];
                        return (
                            <MenuItem
                                key={s}
                                onClick={() => handleStatusClick(s)}
                                sx={{ py: '8px', px: '14px', gap: '10px' }}>
                                <Box
                                    sx={{
                                        width: 10,
                                        height: 10,
                                        borderRadius: '50%',
                                        backgroundColor: sColors.background,
                                        flexShrink: 0,
                                    }}
                                />
                                <Typography variant="body2" sx={{ flex: 1 }}>
                                    {label}
                                </Typography>
                                {subs.length > 0 && <ChevronRightIcon sx={{ fontSize: 16, color: 'text.secondary' }} />}
                            </MenuItem>
                        );
                    })
                ) : (
                    <>
                        <MenuItem
                            onClick={() => setSelectedStatus(null)}
                            sx={{
                                py: '8px',
                                px: '14px',
                                gap: '8px',
                                borderBottom: theme => `1px solid ${theme.palette.divider}`,
                            }}>
                            <ArrowBackIcon sx={{ fontSize: 16 }} />
                            <Box
                                sx={{
                                    width: 10,
                                    height: 10,
                                    borderRadius: '50%',
                                    backgroundColor: selectedStatusColors.background,
                                    flexShrink: 0,
                                }}
                            />
                            <Typography variant="body2" fontWeight={600}>
                                {selectedStatusColors.label}
                            </Typography>
                        </MenuItem>
                        {subStatuses.map(sub => {
                            const subLabel =
                                (isPremium
                                    ? premiumJobApplicationSubStatusLabels[selectedStatus]?.[sub]
                                    : jobApplicationSubStatusLabels[
                                          sub as keyof typeof jobApplicationSubStatusLabels
                                      ]) ?? '';
                            const isSubRejected = isPremium
                                ? PREMIUM_REJECTION_SUB_STATUSES.has(sub as PremiumJobApplicationSubStatus)
                                : REJECTION_SUB_STATUSES.has(sub as JobApplicationSubStatus);
                            return (
                                <MenuItem
                                    key={sub}
                                    onClick={() => handleSubStatusClick(sub)}
                                    sx={{ py: '8px', px: '14px' }}>
                                    <Typography
                                        variant="body2"
                                        sx={isSubRejected ? { color: 'error.main' } : undefined}>
                                        {subLabel}
                                    </Typography>
                                </MenuItem>
                            );
                        })}
                    </>
                )}
            </Menu>

            {pendingBookingStatus && jobApplication && (
                <PremiumBookingDialog
                    open={bookingDialogOpen}
                    onClose={handleBookingClose}
                    jobApplication={jobApplication}
                    premiumStatus={pendingBookingStatus.status}
                    premiumSubStatus={pendingBookingStatus.subStatus}
                    mode={bookingMode}
                    onComplete={handleBookingComplete}
                />
            )}

            {fileShareDialogOpen && fileShareTalentId && (
                <FileManagerModal
                    isOpen={fileShareDialogOpen}
                    onClose={() => setFileShareDialogOpen(false)}
                    talentId={fileShareTalentId}
                    proId={fileShareProId}
                    selectable
                />
            )}
        </>
    );
};

export default ChangeJobApplicationStatusButton;
